import { useNavigate } from 'react-router-dom';
import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { isSafari, Status, StatusType, useChainData } from 'modules/common';
import { TaskRoutesConfig } from 'modules/task';

import { GetTasksItem } from '../../actions/getTasks';
import { AddressCell } from './AddressCell';
import { AmountSpentCell } from './AmountSpentCell';
import { NameCell } from './NameCell';

interface Props {
  task: GetTasksItem;
}

export function Row({ task }: Props): JSX.Element {
  const navigate = useNavigate();
  const { classes, cx } = useStyles();
  const {
    id,
    name,
    cronTarget: address,
    isPaused,
    amountSpent,
    chainId,
  } = task;
  const chainData = useChainData(chainId);

  const tableCellClasses = cx(
    classes.tableCell,
    isSafari() && classes.safariTableCell,
  );

  return (
    <TableRow
      data-tid={`dashboard-task-${id}`}
      className={classes.tableRow}
      hover
      onClick={() =>
        chainData?.chainUnit &&
        navigate(
          TaskRoutesConfig.editTask.generatePath(id, chainData.chainUnit),
        )
      }
    >
      <TableCell className={tableCellClasses}>
        {chainId && <NameCell name={name} chainId={chainId} />}
      </TableCell>

      <TableCell className={tableCellClasses}>
        <Status isError={isPaused} type={StatusType.Task} />
      </TableCell>

      <TableCell className={tableCellClasses}>
        {address && <AddressCell address={address} />}
      </TableCell>

      <TableCell className={tableCellClasses} align="right">
        <AmountSpentCell
          amount={amountSpent}
          unit={chainData?.tokenName ?? ''}
        />
      </TableCell>
    </TableRow>
  );
}

const useStyles = makeStyles()(theme => ({
  tableRow: {
    position: 'relative',
    cursor: 'pointer',

    [`&&:hover > td:first-child:before, &&:hover > td:first-child:before`]: {
      boxShadow: '0 1px 9px 1px rgba(0, 0, 0, 0.28)',
      webkitBoxShadow: '0 1px 9px 1px rgba(0, 0, 0, 0.28)',
    },
  },

  tableCell: {
    '& > *': {
      position: 'relative',
    },

    '&&:first-child:before, &&:first-child:before': {
      boxSizing: 'border-box',
      content: '""',
      position: 'absolute',
      /* do not use top and bottom */
      top: 0,
      left: 0,
      right: 2,

      display: 'block',
      height: '100%',
      borderRadius: 16,
      backgroundColor: theme.palette.common.white,
      transition: '0.3s',
    },
  },

  safariTableCell: {
    '&&:first-child:before, &&:first-child:before': {
      display: 'none',
    },
  },
}));
